/**
 * Parties PURES du protocole anti-triche du Live School.
 *
 * Le contenu des questions est pré-téléchargé CHIFFRÉ (AES-256-CBC) ; les clés
 * sont libérées une étape à la fois. La clé de l'étape `n` est dérivée côté
 * serveur par `Ks(n) = HMAC-SHA256(masterSecret, "${challengeId}:${n}")` — la
 * partie crypto (HMAC/AES, async, Web Crypto) vit dans `_shared/live-crypto.ts`
 * (Deno) et le hook client ; ici on n'expose que la logique déterministe et
 * testable : le message à signer, l'encodage hex, et le « gate » temporel.
 *
 * ⚠️ Le secret maître ne transite JAMAIS par ce module ni par le client.
 */

import { stepOffsetMs } from './quiz-state';

/** Message HMAC dont la signature donne la clé AES de l'étape `n` (0-based). */
export function stepKeyMessage(challengeId: string, stepIndex: number): string {
  return `${challengeId}:${stepIndex}`;
}

/** Octets → chaîne hexadécimale (minuscule). */
export function bytesToHex(bytes: Uint8Array): string {
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  return hex;
}

/** Chaîne hexadécimale → octets. Lève si la longueur est impaire ou non-hex. */
export function hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
  const clean = hex.trim().toLowerCase();
  if (clean.length % 2 !== 0) throw new Error('hex_length_odd');
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) {
    const byte = Number.parseInt(clean.slice(i * 2, i * 2 + 2), 16);
    if (Number.isNaN(byte)) throw new Error('hex_invalid');
    out[i] = byte;
  }
  return out;
}

/**
 * Vrai si l'étape `stepIndex` (0-based) est DÉVERROUILLÉE à l'instant serveur
 * `now` : son horloge a dépassé le timestamp de début de l'étape.
 *   now ≥ startsAt + stepIndex·D
 * C'est la garde que `live-key` applique avant de renvoyer la clé.
 */
export function isStepUnlocked(
  now: number,
  startsAt: number,
  stepIndex: number,
  stepDurationMs: number,
  stepDurationsMs?: readonly number[] | null,
): boolean {
  return now >= startsAt + stepOffsetMs(stepIndex, stepDurationMs, stepDurationsMs);
}

// ── Primitives Web Crypto (HMAC + AES-256-CBC) ───────────────────────────────
// Identiques en Node (Vitest), navigateur et Deno → la partie async vit ici
// (testable round-trip) et est mirrorée dans `_shared/live-crypto.ts` pour les
// edge fns Deno. Le SERVEUR dérive/chiffre ; le CLIENT ne fait que déchiffrer
// (il reçoit la clé hex de `live-key`, jamais le secret maître).

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/** Octets → base64 (binaire). */
export function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

/** base64 → octets (backé par ArrayBuffer, requis par Web Crypto sous TS 5.7). */
export function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Clé AES-256 (32 octets) de l'étape `n` : `HMAC-SHA256(masterSecret, "${id}:${n}")`.
 * SERVEUR uniquement (le secret maître ne quitte jamais l'edge fn). `live-key`
 * renvoie `bytesToHex(...)` de cette clé une fois l'étape déverrouillée.
 */
export async function deriveStepKey(
  masterSecret: string,
  challengeId: string,
  stepIndex: number,
): Promise<Uint8Array<ArrayBuffer>> {
  const hmacKey = await crypto.subtle.importKey(
    'raw',
    textEncoder.encode(masterSecret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign(
    'HMAC',
    hmacKey,
    textEncoder.encode(stepKeyMessage(challengeId, stepIndex)),
  );
  return new Uint8Array(sig); // 32 octets → AES-256
}

export interface EncryptedStep {
  /** IV (16 octets) en base64. */
  iv: string;
  /** Texte chiffré en base64. */
  ciphertext: string;
}

/** Chiffre un texte en AES-256-CBC avec un IV aléatoire (16 octets). SERVEUR. */
export async function aesCbcEncrypt(
  keyBytes: Uint8Array<ArrayBuffer>,
  plaintext: string,
): Promise<EncryptedStep> {
  const iv = crypto.getRandomValues(new Uint8Array(16));
  const key = await crypto.subtle.importKey('raw', keyBytes, { name: 'AES-CBC' }, false, [
    'encrypt',
  ]);
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv },
    key,
    textEncoder.encode(plaintext),
  );
  return { iv: bytesToBase64(iv), ciphertext: bytesToBase64(new Uint8Array(ct)) };
}

/** Déchiffre une étape AES-256-CBC. Côté CLIENT, `keyBytes = hexToBytes(clé live-key)`. */
export async function aesCbcDecrypt(
  keyBytes: Uint8Array<ArrayBuffer>,
  ivB64: string,
  ciphertextB64: string,
): Promise<string> {
  const key = await crypto.subtle.importKey('raw', keyBytes, { name: 'AES-CBC' }, false, [
    'decrypt',
  ]);
  const pt = await crypto.subtle.decrypt(
    { name: 'AES-CBC', iv: base64ToBytes(ivB64) },
    key,
    base64ToBytes(ciphertextB64),
  );
  return textDecoder.decode(pt);
}
